import { useEffect, useState } from "react";

// Smooth-scrolls to the chapter referenced by the URL hash (or to top when absent).
// NOTE (lint): `el` and `t` are created inside the effect on each run — they are
// not external reactive values and must not appear in the dependency array.
export function useScrollToHash(hash) {
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        const t = setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          60
        );
        return () => clearTimeout(t);
      }
      return undefined;
    }
    window.scrollTo({ top: 0, behavior: "instant" });
    return undefined;
  }, [hash]);
}

// Tracks which chapter is currently in view via IntersectionObserver.
// NOTE (lint): `IntersectionObserver` is a browser global; `a`, `b`, `entries`,
// `sections`, `obs`, etc. are all created inside the effect/callbacks — `chapters`
// is the only external reactive dependency.
export function useActiveChapter(chapters) {
  const [activeId, setActiveId] = useState(chapters[0]?.id);

  useEffect(() => {
    const sections = chapters
      .map((c) => document.getElementById(c.id))
      .filter(Boolean);
    if (!sections.length) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-25% 0px -55% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [chapters]);

  return activeId;
}
